import { RootState, ThunkDispatch } from "core-integration/src/store";
import { getEntry } from "core-integration/src/store/entries/reducers";
import { getNextEvent } from "core-integration/src/store/events/reducers";
import { getRules } from "core-integration/src/store/game/reducers";
import { getJoinPublicLeagueError } from "core-integration/src/store/leagues/reducers";
import { joinPublicLeague } from "core-integration/src/store/leagues/thunks";
import { getPlayerData } from "core-integration/src/store/player/reducers";
import { ILoggedInPlayer } from "core-integration/src/store/player/types";
import { ErrorAlert } from "plos/src/components/alerts";
import { Button } from "plos/src/components/buttons/Button";
import CallToAction from "plos/src/components/CallToAction";
import HorizontalDivider from "plos/src/components/HorizontalDivider";
import SectionHeader from "plos/src/components/SectionHeader";
import { SurfaceContainer } from "plos/src/components/SurfaceContainer";
import { useLocalizedNavigate } from "plos/src/hooks/useLocalizedNavigate";
import { rightSidebarLayout } from "plos/src/layouts";
import { useEffect } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useTrackingContext } from "../../../contexts/TrackingContext";
import HelmetHeadLeagues from "../../leagues/HelmetHeadLeagues";
import {
  formItem,
  gridDivider,
  leagueGridLayout,
  leaguesMainContent,
  noteTextStyle,
} from "../leaguesAndCups.css";

const JoinPublic = () => {
  const { t } = useTranslation("leaguesAndCups");
  const { fireClickTrackEvent, firePageViewEvent } = useTrackingContext();
  const dispatch = useDispatch<ThunkDispatch>();
  const navigate = useLocalizedNavigate();

  const player = useSelector(
    (state: RootState) => getPlayerData(state) as ILoggedInPlayer,
  ); // enforced by EntryRoute
  const playerEntry = player.entry;
  const entry = useSelector((state: RootState) => getEntry(state, playerEntry));
  const error = useSelector(getJoinPublicLeagueError);
  const nxt = useSelector(getNextEvent);
  const rules = useSelector(getRules);

  useEffect(() => {
    firePageViewEvent("fantasy leagues & cups", "join public league");
  }, []);

  useEffect(() => {
    if (!nxt) {
      navigate("/", { replace: true });
    }
  }, [navigate, nxt]);

  const handleOnJoinClassic = async () => {
    await dispatch(joinPublicLeague({ scoring: "c" }));
    fireClickTrackEvent(
      {
        event_category: "fantasy league",
        event_component: "fantasy classic clicks",
        event_detail: "join classic cta",
        event_type: "join public league",
      },
      "fantasy join public league",
    );
    navigate("/leagues");
  };
  const handleOnJoinH2H = async () => {
    await dispatch(joinPublicLeague({ scoring: "h" }));
    fireClickTrackEvent(
      {
        event_category: "fantasy league",
        event_component: "fantasy classic clicks",
        event_detail: "join h2h cta",
        event_type: "join public league",
      },
      "fantasy join public league",
    );
    navigate("/leagues");
  };

  const nonFieldError = error?.badRequest?.non_field_errors?.[0];
  const errorMessage =
    nonFieldError?.code === "public_league_max_exceeded"
      ? `You can only enter ${rules.league_join_public_max} public leagues`
      : JSON.stringify(error);

  if (!entry) {
    return null;
  }

  return (
    <>
      <HelmetHeadLeagues />
      <div className={rightSidebarLayout}>
        <SurfaceContainer>
          <div className={leaguesMainContent}>
            {nonFieldError && <ErrorAlert>{errorMessage}</ErrorAlert>}
            <SectionHeader
              title={t(
                "joinPublic.publicLeagues.heading",
                "Join a Public League",
              )}
              description={
                t(
                  "joinPublic.publicLeagues.description",
                  "Public leagues allow you to compete against {{maxSizePublicClassic}} randomly assigned game players in a classic league and up to {{maxSizePublicH2H}} in head-to-head leagues. You can join up to {{leagueJoinPublicMax}} public leagues.",
                  {
                    maxSizePublicClassic: rules.league_max_size_public_classic,
                    maxSizePublicH2H: rules.league_max_size_public_h2h,
                    leagueJoinPublicMax: rules.league_join_public_max,
                  },
                ) as string
              }
            />
            <div className={leagueGridLayout}>
              <p className={noteTextStyle}>
                <Trans
                  ns="leaguesAndCups"
                  i18nKey="joinPublic.publicLeagues.note"
                >
                  Note, you can't remove your team from a public league after
                  the league has started, once the challenge is on there's no
                  quitting.
                </Trans>
              </p>
              <div className={formItem}>
                <CallToAction
                  heading={t(
                    "joinPublic.publicLeagues.classicScoring.title",
                    "Classic Scoring",
                  )}
                  description={t(
                    "joinPublic.publicLeagues.classicScoring.description",
                    "In a league with classic scoring, teams are ranked based on their total points in the game. You can join or leave a league with classic scoring at any point during the season.",
                  )}
                >
                  <Button onPress={handleOnJoinClassic}>
                    {t(
                      "joinPublic.publicLeagues.classicScoring.joinClassicButton",
                      "Join Classic League",
                    )}
                  </Button>
                </CallToAction>
              </div>
              <div className={gridDivider}>
                <HorizontalDivider dataContent="or" />
              </div>
              <div className={formItem}>
                <CallToAction
                  heading={t(
                    "joinPublic.publicLeagues.h2hScoring.title",
                    "Head-to-Head Scoring",
                  )}
                  description={t(
                    "joinPublic.publicLeagues.h2hScoring.description",
                    "In a league with head-to-head scoring, every team plays a match against another team in the league each Round. The match result is based on the Round score of each team minus any transfer points spent preparing for the Round.",
                  )}
                >
                  <Button onPress={handleOnJoinH2H}>
                    {t(
                      "joinPublic.publicLeagues.h2hScoring.joinH2HButton",
                      "Join Head-to-Head League",
                    )}
                  </Button>
                </CallToAction>
              </div>
            </div>
          </div>
        </SurfaceContainer>
      </div>
    </>
  );
};

export default JoinPublic;
